import React, { useMemo } from 'react';
import styles from './DebtProducts.module.scss';
import useLegacyBusinessDataFetch from '../DataRoom/useLegacyBusinessDataFetch';
import { Button } from '../../common/_custom';
import {
    caretRight,
    ctaGrey,
    debtProdFilters,
    debtProductsEmpty,
    editIconPurple,
    editProfileIcon,
    NOT_ALLOWED,
    PURPLE_CROSS,
} from '../../assets/hostedassets';
import LoadingSpinner from '../../common/_custom/LoadingSpinner/LoadingSpinner';
import ManageDealDrawer from '../../common/ManageDealDrawer/ManageDealDrawer';
import { setDealDrawer } from '../../store/investor/action';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import clsx from 'clsx';
import ProbabilityBar from './components/ProbabilityBar/ProbabilityBar';
import probabilityBarStyles from './components/ProbabilityBar/ProbabilityBar.module.scss';
import DebtProductOverview from './components/DebtProductOverview/DebtProductOverview';
import Tooltip from '../../common/_custom/Tooltip/Tooltip';
import { DebtProductRowData } from './debtProductTypes';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useDebtProductLogic from './useDebtProductLogic';
import { USER_TYPE } from '../../utils/constants/user';
import { isEmpty } from '../../utils/utils';
import AIText from '../../common/AIText/AIText';
import { formatDateByFormat, DATE_FORMATS } from '../../utils/dateUtils';
import { setDebtProductsFilters } from '../../store/company/action';
import { toggleProductExpand } from '../../store/debtProducts/action';
import DealSuccessModal from '../../common/DealApplicationDrawer/Components/DealSuccessModal/DealSuccessModal';
import SecurityModal from './components/SecurityModal/SecurityModal';
import AskAicaV2 from '../Dashboard/DashboardModules/Overview/components/AskAicaV2/AskAicaV2';
import { useVisibilityConfig } from '../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import MandateDropdown from './components/MandateDropdown/MandateDropdown';
import MandateSharingDrawer from './components/MandateSharingDrawer/MandateSharingDrawer';
import ShareWithLenderModal from './components/ShareWithLenderModal/ShareWithLenderModal';
import {
    formatCurrentValue,
    formatOriginalValue,
    getTooltipContent,
    getCurrentIsShared,
    getMetricStylingInfo,
    getMetricSourceForField,
    filterVisibleHeaders,
} from './DebtProductsUtil';
import {
    ButtonText,
    DATA_SOURCE_FOR_LAST_UPDATED_DATA_TYPE,
    DEAL_SHARED_FOR_ANOTHER_PRODUCT,
    DealPayloadScenario,
    ENTITY_TYPE_KEYS,
    getEditableFieldConfig,
    NAVIGATION_CONSTANTS,
    originalKeyMap,
    PRODUCT_TERMS_KEYS,
    RECOMMENDED_VALUE_TOOLTIP_CONTENT,
} from './DebtProductsConstants';
import { DATA_SOURCE_TYPE } from '../../common/ManageDealDrawer/DealDrawerConstants';
import MandateAggregatedTermsComponent from './components/MandateAggregatedTerms/MandateAggregatedTerms';
import mandateTermsStyles from './components/MandateAggregatedTerms/MandateAggregatedTerms.module.scss';
import { NumberCellRenderer } from '../../common/_custom/DataTable/components/CellRenderers/NumberCellRenderer/NumberCellRenderer';
import { DataTableColumnType } from '../../common/_custom/DataTable/DataTable.types';
import UnsavedChangesModal from './components/UnsavedChangesModal/UnsavedChangesModal';
import { ShimmerThumbnail } from 'react-shimmer-effects';
import { TAB_TO_TASK_TRACKER_MAP } from '../../utils/constants/commonConstants';

function DebtProducts() {
    const dispatch = useAppDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const { selectedCompany } = useLegacyBusinessDataFetch();
    const header = document.getElementById('header') ?? document.createElement('div');
    const {
        loading, //component loading state
        noPecExists, //true if there are no existing PECs
        companyMetricsDataExists,
        companyMetricsData,
        metricsMetadata,
        showDetailOverview, //used to store row data for opening modal
        debtProductsData, //data used to render table
        showEmptyScreen, //boolean to show empty screen
        dealDrawerState,
        setShowDetailOverview,
        fetchPECDataAndDebtProductsData,
        fetchCompanyMetrics,
        updateProductTerms,
        updateLenderTerms,
        dealPayload,
        saveSelection,
        dealId,
        savingDraft,
        fetchDebtProductsData,
        //MIXPANEL
        registerMixpanelForRowExpand,
        registerMixpanelForUserHelpClick,
        registerMixpanelForEditProfileClicked,
        showFilters,
        renderDealActionButton,
        renderClearOrDeleteButton,
        dealStatus,
        draftDealsData,
        dealStatusConfig,
        selectedMandate,
        handleMandateSelection,
        percentageCompletion,
        getCheckboxState,
        countSelectedLenderCount,
        aggregatedMandateTerms,
        isMandateSharingDrawerOpen,
        setIsMandateSharingDrawerOpen,
        isShareWithLenderModalOpen,
        setIsShareWithLenderModalOpen,
        productTerms,
        lenderTerms,
        dealTerms,
        updateEndUseOfCapital,
        addDealNote,
        updateDealDocuments,
        selectedUsers,
        updateSelectedUsers,
        updateEditableField,
        isRowCalculating,
        isRowShimmering,
        isTransitioning,
        //Share success modal
        showShareSuccessModal,
        setShowShareSuccessModal,
        //Security modal
        showSecurityModal,
        setShowSecurityModal,
        updateSecurityOffered,
        //Probability tooltip
        getProbabilityTooltip,
        // Unsaved changes modal
        showUnsavedChangesModal,
        handleActionWithUnsavedCheck,
        handleCloseWithoutSaving,
        handleSaveChanges,
        handleCloseUnsavedModal,
        registerMixPanelForFiltersOnDebtOptions,
        registerMixpanelForEditTerms,
        activeCompletionPercentage,
        // Row hover state for editable fields
        setEditableRowHover,
        isEditableRowHovered,
        getCalculatingRowKey,
    } = useDebtProductLogic({ params, selectedCompany });

    const { isVisible } = useVisibilityConfig();

    const user = useAppSelector((state) => state.user);
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const debtFilters = useAppSelector((state) => state.company.debtProductsFilters);
    const isCompanySide = user.userData.accountType === USER_TYPE.COMPANY || location.pathname.includes('company');
    const showSuccessModal = useAppSelector((state) => state.investor.dealApplicationDrawer.showSuccessModal);
    const taskTrackerMap = useAppSelector((state) => state.investor.taskTrackerMap);
    const lastUpdatedAuditedFinancialsDate = taskTrackerMap?.[TAB_TO_TASK_TRACKER_MAP.auditedFinancials[0]]?.dateCreated;
    const isPartnerSide = user.userData.accountType === USER_TYPE.PARTNER;
    const assesseeOrgId = params.id;
    const lastUpdatedInfo = useMemo(() => {
        const borrowerFinancials = companyMetricsData?.borrowerFinancials;
        const userName = metricsMetadata?.userName;

        if (borrowerFinancials?.dataSource) {
            if (!userName) return null;
            const { dataSource, lastUpdatedAt } = borrowerFinancials;
            const dataType =
                dataSource === DATA_SOURCE_TYPE.DISCUSSION
                    ? DATA_SOURCE_FOR_LAST_UPDATED_DATA_TYPE.DISCUSSION_DATA
                    : DATA_SOURCE_FOR_LAST_UPDATED_DATA_TYPE.ACTUAL_DATA;

            return {
                prefix: `Last updated by ${userName} on ${formatDateByFormat(lastUpdatedAt, DATE_FORMATS.DATE_FORMAT_D_MMM_YYYY)}`,
                highlight: `basis ${dataType}`,
            };
        }

        if (!borrowerFinancials?.dataSource && lastUpdatedAuditedFinancialsDate) {
            return {
                prefix: `Last updated by System on ${formatDateByFormat(
                    lastUpdatedAuditedFinancialsDate,
                    DATE_FORMATS.DATE_FORMAT_D_MMM_YYYY,
                )}`,
                highlight: `basis ${DATA_SOURCE_FOR_LAST_UPDATED_DATA_TYPE.AUDITED_DATA}`,
            };
        }

        return null;
    }, [
        metricsMetadata?.userName,
        companyMetricsData?.borrowerFinancials?.dataSource,
        companyMetricsData?.borrowerFinancials?.lastUpdatedAt,
        lastUpdatedAuditedFinancialsDate,
    ]);

    const renderEmptyScreen = () => {
        return (
            <div className={styles.Empty}>
                <img src={debtProductsEmpty} alt={''} height={'100px'} />
                {noPecExists ? (
                    <div className={styles.Text}>
                        {/*<span className={styles.Link} onClick={openCreatePECDrawer}>*/}
                        {/*    Add PEC*/}
                        {/*</span>*/}
                        &nbsp;Please set investment preferences first
                    </div>
                ) : isPartnerSide && debtProductsData.rows.length === 0 ? (
                    <div className={`${styles.Text} ${styles.PartnerEmptyMessage}`}>
                        <div className={styles.BoldLine}>No debt options available at the moment</div>
                        <div className={styles.NormalLine}>
                            Please ensure AICA report is unlocked or company financials is completely filled in{' '}
                            <span
                                className={styles.Link}
                                onClick={() => {
                                    dispatch(
                                        setDealDrawer({
                                            open: true,
                                            drawerLabel: 'Edit Profile',
                                            companyName: selectedCompany?.orgName,
                                        }),
                                    );
                                    registerMixpanelForEditProfileClicked('Empty Screen');
                                }}
                            >
                                Edit Profile
                            </span>{' '}
                        </div>
                    </div>
                ) : companyMetricsDataExists ? (
                    <></>
                ) : (
                    isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.EDIT_PROFILE) && (
                        <div className={styles.Text}>
                            <span
                                className={styles.Link}
                                onClick={() => {
                                    dispatch(
                                        setDealDrawer({
                                            open: true,
                                            drawerLabel: 'Edit Profile',
                                            companyName: selectedCompany?.orgName,
                                        }),
                                    );
                                    registerMixpanelForEditProfileClicked('Empty Screen');
                                }}
                            >
                                Edit Profile
                            </span>
                            &nbsp;to view products with probability
                        </div>
                    )
                )}
            </div>
        );
    };

    const showLoader = () => (
        <div className={styles.Loader}>
            <LoadingSpinner color="#6021B3" height={'40px'} />
            <div className={styles.Text}>{`Fetching ${debtProductsData.visibilityStates.debtProductName}`}</div>
        </div>
    );

    const renderRowItem = (headerId, rowData: DebtProductRowData, isParentRow = false) => {
        const isShimmeringRow = !isParentRow && isRowShimmering(rowData, isParentRow);
        const lenderIsShared =
            !isParentRow && rowData?.lenderId && rowData?.productId && lenderTerms[rowData.lenderId]?.[rowData.productId]?.isShared;
        const isLenderStrikethrough = lenderIsShared === -1;
        const isLenderGrayed = lenderIsShared !== undefined && lenderIsShared !== 0;
        const isFieldDisabled = isParentRow
            ? productTerms[rowData.productId]?.isShared !== undefined &&
              productTerms[rowData.productId]?.isShared !== 0 &&
              productTerms[rowData.productId]?.isShared !== 2
            : rowData.lenderId &&
              lenderTerms[rowData.lenderId]?.[rowData.productId]?.isShared !== undefined &&
              lenderTerms[rowData.lenderId]?.[rowData.productId]?.isShared !== 0;
        if (headerId === ENTITY_TYPE_KEYS.CARET && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
            return null;
        }

        if (headerId === ENTITY_TYPE_KEYS.SELECT) {
            if (
                (isParentRow && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT)) ||
                (!isParentRow && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER))
            ) {
                return null;
            }
        }

        let renderComponent = <></>;
        switch (headerId) {
            case ENTITY_TYPE_KEYS.PRODUCT: {
                renderComponent = (
                    <div
                        className={clsx(styles.Product, {
                            [styles.ProductSub]: !isParentRow,
                        })}
                    >
                        {isShimmeringRow ? (
                            <ShimmerThumbnail height={20} width={150} rounded className={styles.LenderShimmer} />
                        ) : isLenderStrikethrough ? (
                            <Tooltip content={DEAL_SHARED_FOR_ANOTHER_PRODUCT} direction="top" fitContent usePortal={true}>
                                <span className={clsx(styles.Strikethrough, styles.GrayedText)}>
                                    {(isParentRow ? rowData?.[headerId] : rowData?.['lenderName']) || '-'}
                                </span>
                            </Tooltip>
                        ) : (
                            <span
                                className={clsx({
                                    [styles.GrayedText]: !isParentRow && isLenderGrayed,
                                })}
                            >
                                {(isParentRow ? rowData?.[headerId] : rowData?.['lenderName']) || '-'}
                            </span>
                        )}
                        {!isParentRow && !isShimmeringRow && rowData?.isNegativeLender && (
                            <img src={NOT_ALLOWED} alt="na" height={'16px'} className={styles.NotAllowedIcon} />
                        )}
                        {isParentRow && !!rowData?.lenders?.length && (
                            <div className={styles.Count}>
                                {countSelectedLenderCount(rowData)}/{rowData?.lenders?.length}
                            </div>
                        )}
                    </div>
                );
                break;
            }
            case ENTITY_TYPE_KEYS.SELECT:
                if (isShimmeringRow && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER)) {
                    renderComponent = <ShimmerThumbnail height={16} width={16} rounded className={styles.LenderShimmer} />;
                } else if (
                    (isParentRow && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT)) ||
                    (!isParentRow && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER))
                ) {
                    const { checked, disabled } = getCheckboxState(rowData, isParentRow);
                    renderComponent = (
                        <input
                            type="checkbox"
                            className={styles.Checkbox}
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) => {
                                e.stopPropagation();
                                if (isParentRow) {
                                    updateProductTerms(rowData.productId);
                                } else {
                                    if (rowData.lenderId) {
                                        updateLenderTerms(rowData.lenderId, rowData.productId);
                                    }
                                }
                            }}
                        />
                    );
                } else {
                    renderComponent = <></>;
                }
                break;
            case ENTITY_TYPE_KEYS.CARET:
                if (isShimmeringRow && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
                    renderComponent = <ShimmerThumbnail height={14} width={14} rounded className={styles.LenderShimmer} />;
                } else if (isParentRow && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
                    renderComponent = (
                        <div
                            className={clsx(styles.CaretDiv, {
                                [styles.CaretDivHov]: rowData.expand,
                            })}
                        >
                            <img
                                className={clsx(styles.CaretImg, {
                                    [styles.ExpandedCaret]: rowData?.expand && isParentRow,
                                    [styles.DisabledCaret]: !rowData?.lenders?.length,
                                })}
                                src={caretRight}
                                height={'14px'}
                                alt={''}
                                style={{ marginTop: '4px' }}
                            />
                        </div>
                    );
                }
                break;
            case ENTITY_TYPE_KEYS.PROBABILITY: {
                const isCalculating = isRowCalculating(rowData, isParentRow);
                const probabilityRow = (
                    <div className={styles.ProbabilityRow} style={{ opacity: 1 }}>
                        {isCalculating ? (
                            <AIText text="Calculating..." style={{ fontSize: '12px' }} />
                        ) : isShimmeringRow ? (
                            <ShimmerThumbnail height={20} width={100} rounded className={styles.LenderShimmer} />
                        ) : (
                            <>
                                <ProbabilityBar
                                    probability={rowData?.[headerId]}
                                    pecThreshold={rowData?.pecValues?.PEC_THRESHOLD_PERCENTAGE}
                                />
                                <img src={ctaGrey} alt={''} height={'12px'} />
                            </>
                        )}
                    </div>
                );
                const tooltipData = getProbabilityTooltip(rowData?.[headerId], rowData?.pecValues?.PEC_THRESHOLD_PERCENTAGE);

                renderComponent = tooltipData ? (
                    <Tooltip
                        content={
                            <div className={probabilityBarStyles.TooltipContent}>
                                <span className={probabilityBarStyles.TooltipText}>{tooltipData.message}</span>
                                {tooltipData.recommendedPercentage && (
                                    <span className={probabilityBarStyles.TooltipRecommended}>
                                        Recommended: {tooltipData.recommendedPercentage}
                                    </span>
                                )}
                            </div>
                        }
                        direction="top"
                        usePortal={true}
                        style={{ width: '200px', maxWidth: '250px' }}
                        containerFullWidth={false}
                    >
                        {probabilityRow}
                    </Tooltip>
                ) : (
                    probabilityRow
                );
                break;
            }
            case ENTITY_TYPE_KEYS.SECURITY: {
                const totalElements = rowData?.security?.length || 0;

                if (isShimmeringRow) {
                    renderComponent = <ShimmerThumbnail height={20} width={120} rounded className={styles.LenderShimmer} />;
                } else {
                    const isRowHovered = isEditableRowHovered(rowData, isParentRow);
                    const metricSource = getMetricSourceForField(rowData, 'security');
                    const hasValue = totalElements > 0;
                    const rowKey = getCalculatingRowKey(rowData, isParentRow);

                    const currentIsShared = getCurrentIsShared(isParentRow, rowData, productTerms, lenderTerms);
                    const { isBREOrPEC, isUserOverridden, colorClass } = getMetricStylingInfo(metricSource, currentIsShared, styles);

                    renderComponent = (
                        <Tooltip
                            key={rowKey}
                            content={getTooltipContent(rowData?.security, styles)}
                            direction={'top'}
                            fitContent
                            style={{
                                left: '0',
                                transform: 'translateX(0)',
                                bottom: '110%',
                                top: 'auto',
                            }}
                            contentCss={{
                                maxHeight: '600px',
                                overflowY: 'auto',
                            }}
                            usePortal={true}
                        >
                            <div
                                className={clsx(styles.SecurityContainerDefault, {
                                    [styles.SecurityContainerDisabled]: isFieldDisabled,
                                    [styles.SecurityContainerRowHovered]: !isFieldDisabled && isRowHovered,
                                })}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isFieldDisabled) return;
                                    setShowSecurityModal({
                                        securityData: rowData?.security,
                                        productId: rowData?.productId,
                                        productName: isParentRow ? rowData?.productName : rowData?.lenderName || '',
                                        lenderId: rowData?.lenderId,
                                        isParentRow,
                                        pecSecurity: rowData?.pecValues?.PEC_SECURITY as string[][] | undefined,
                                        mandateId: dealId || '',
                                    });
                                }}
                                style={{ cursor: isFieldDisabled ? 'default' : 'pointer' }}
                            >
                                {hasValue ? (
                                    rowData?.security?.slice(0, 2).map((sec, i) => {
                                        const isLastVisible = i === 1;
                                        const remainingCount = isRowHovered ? totalElements - 1 : totalElements - 2;
                                        const showMore = isLastVisible && (remainingCount > 0 || (isRowHovered && totalElements > 1));
                                        return (
                                            <div key={i} className={styles.SecurityRowDefault}>
                                                <span
                                                    className={clsx(styles.SecurityItemDefault, {
                                                        [styles.SecurityItemDisabled]: isFieldDisabled,
                                                        [styles.italicText]: isBREOrPEC,
                                                        [styles.normalText]: !isUserOverridden,
                                                        [colorClass]: isUserOverridden,
                                                    })}
                                                >
                                                    <span className={styles.SecurityText}>{sec?.type || '-'}</span>
                                                </span>
                                                {showMore && (
                                                    <span className={styles.More}>
                                                        +{isRowHovered ? totalElements - 1 : totalElements - 2} more
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : !isFieldDisabled && isRowHovered ? (
                                    <>
                                        <div className={styles.SecurityAddRow}>
                                            <span className={styles.SecurityAddText}>Add</span>
                                        </div>
                                        <img src={editIconPurple} alt="Add" className={styles.SecurityEditIcon} />
                                    </>
                                ) : (
                                    '-'
                                )}
                                {!isFieldDisabled && isRowHovered && hasValue && (
                                    <img src={editIconPurple} alt="Edit" className={styles.SecurityEditIcon} />
                                )}
                            </div>
                        </Tooltip>
                    );
                }
                break;
            }
            case PRODUCT_TERMS_KEYS.AMOUNT:
            case PRODUCT_TERMS_KEYS.TENURE:
            case PRODUCT_TERMS_KEYS.ROI: {
                const fieldConfig = getEditableFieldConfig(headerId);
                const isCalculating = isRowCalculating(rowData, isParentRow);
                if (fieldConfig) {
                    const originalKey = originalKeyMap[headerId];
                    const originalValue = rowData?.[originalKey];
                    const formattedOriginalValue = formatOriginalValue(originalValue, headerId, fieldConfig);
                    const formattedCurrentValue = formatCurrentValue(rowData, headerId, fieldConfig);
                    const metricSource = getMetricSourceForField(rowData, headerId);
                    const currentIsShared = getCurrentIsShared(isParentRow, rowData, productTerms, lenderTerms);
                    const { isBREOrPEC, isUserOverridden, colorClass } = getMetricStylingInfo(metricSource, currentIsShared, styles);

                    renderComponent = isCalculating ? (
                        <AIText
                            text={formattedCurrentValue}
                            animate={true}
                            style={{
                                background: 'linear-gradient(90deg, #FFFFFF -13.76%, #4D4D4D 26.14%, #4D4D4D 93.76%)',
                                backgroundSize: '200% 200%',
                                WebkitTextFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                fontSize: '14px',
                            }}
                        />
                    ) : isShimmeringRow ? (
                        <ShimmerThumbnail height={20} width={120} rounded className={styles.LenderShimmer} />
                    ) : (
                        <div className={styles.NumberCellRendererContainer}>
                            {formattedOriginalValue && !isLenderStrikethrough && (
                                <Tooltip
                                    content={RECOMMENDED_VALUE_TOOLTIP_CONTENT}
                                    direction="top"
                                    fitContent
                                    style={{ left: '0', transform: 'translateX(-50%)', bottom: '110%', top: 'auto', minWidth: '160px' }}
                                    contentCss={{ textAlign: 'center', fontSize: '12px' }}
                                >
                                    <span
                                        className={clsx(
                                            styles.OriginalValueContainer,
                                            styles.Strikethrough,
                                            styles.GrayedText,
                                            styles.originalValueItalic,
                                        )}
                                    >
                                        {formattedOriginalValue}
                                    </span>
                                </Tooltip>
                            )}
                            <span
                                className={clsx({
                                    [styles.Strikethrough]: isLenderStrikethrough,
                                    [styles.GrayedText]: isLenderStrikethrough,
                                    [styles.italicText]: isBREOrPEC,
                                    [styles.boldText]: isUserOverridden,
                                    [colorClass]: isUserOverridden,
                                })}
                                onFocus={() => {
                                    registerMixpanelForEditTerms(headerId, rowData, isParentRow);
                                }}
                            >
                                <NumberCellRenderer
                                    column={{
                                        field: fieldConfig.field,
                                        headerLabel: fieldConfig.headerLabel,
                                        headerOriginalLabel: fieldConfig.headerLabel,
                                        type: DataTableColumnType.NUMBER,
                                        unit: fieldConfig.unit,
                                        unitLeft: fieldConfig.unitLeft,
                                        secondaryUnit: fieldConfig.secondaryUnit,
                                        allowDecimal: fieldConfig.allowDecimal,
                                    }}
                                    row={{ id: rowData?.productId || rowData?.lenderId }}
                                    value={rowData?.[headerId]}
                                    rowIndex={0}
                                    columnIndex={0}
                                    dynamicProps={{
                                        updateCustomFieldStatus: (row, update, column, handlers) => {
                                            if (isEmpty(update?.value)) return;
                                            updateEditableField(fieldConfig.field, update.value, rowData, isParentRow, handlers);
                                        },
                                        isDisabled: !!isFieldDisabled,
                                        isRowHovered: isEditableRowHovered(rowData, isParentRow),
                                    }}
                                />
                            </span>
                        </div>
                    );
                }
                break;
            }
            default:
                if (isShimmeringRow) renderComponent = <ShimmerThumbnail height={20} width={80} rounded className={styles.LenderShimmer} />;
                else renderComponent = rowData?.[headerId] ?? '-';
                break;
        }

        return (
            <td
                key={header.id}
                className={clsx({
                    [styles.RowItem]: true,
                    [styles.SelectColInner]: headerId === ENTITY_TYPE_KEYS.SELECT && !isParentRow,
                    [styles.SelectCol]: headerId === ENTITY_TYPE_KEYS.SELECT && isParentRow,
                    [styles.CaretCol]: headerId === ENTITY_TYPE_KEYS.CARET && isParentRow,
                    [styles.CaretColInner]: headerId === ENTITY_TYPE_KEYS.CARET && !isParentRow,
                    [styles.ProductsCol]: headerId === ENTITY_TYPE_KEYS.PRODUCT,
                    [styles.ProbabilityCol]: headerId === ENTITY_TYPE_KEYS.PROBABILITY,
                    [styles.SecurityCol]: headerId === ENTITY_TYPE_KEYS.SECURITY,
                    [styles.DisabledRowItem]: isParentRow && !rowData?.lenders?.length,
                    [styles.NoSelectMode]:
                        headerId === ENTITY_TYPE_KEYS.SELECT &&
                        ((isParentRow && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT)) ||
                            (!isParentRow && !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER))),
                })}
                onClick={(e) => {
                    switch (headerId) {
                        case ENTITY_TYPE_KEYS.CARET:
                            e.stopPropagation();
                            if (isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
                                expandOrCollapseRow(rowData, isParentRow);
                            }
                            break;
                        case ENTITY_TYPE_KEYS.PROBABILITY:
                            e.stopPropagation();
                            setShowDetailOverview(rowData);
                            registerMixpanelForUserHelpClick(rowData, isParentRow);
                            break;
                        case ENTITY_TYPE_KEYS.SELECT:
                            e.stopPropagation();
                            break;
                        default:
                            break;
                    }
                }}
            >
                {renderComponent}
            </td>
        );
    };

    const expandOrCollapseRow = (rowData, isParentRow) => {
        if (isParentRow && rowData?.lenders?.length && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
            if (!rowData.expand) {
                registerMixpanelForRowExpand(rowData);
            }
            dispatch(toggleProductExpand(rowData.productId));
        }
    };

    return (
        <>
            <div
                className={clsx({
                    [styles.DebtProducts]: true,
                    [styles.DebtProductsCompany]: isCompanySide,
                    [styles.NoAccordion]: !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION),
                    [styles.NoSelectProduct]: !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT),
                    [styles.NoSelectLender]: !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER),
                })}
            >
                <div className={styles.ScrollWrapper}>
                    <div className={styles.Container}>
                        <div className={styles.Top}>
                            <div className={styles.TitleSection}>
                                <div className={styles.Title}>{debtProductsData.visibilityStates.debtProductName ?? 'Debt Products'}</div>
                                <MandateDropdown
                                    data={draftDealsData}
                                    dealStatusConfig={dealStatusConfig}
                                    selectedMandate={selectedMandate}
                                    placeholder="Select Mandate"
                                    onSelect={(mandate) => {
                                        handleActionWithUnsavedCheck(() => handleMandateSelection(mandate));
                                    }}
                                    onClear={() => handleActionWithUnsavedCheck(() => handleMandateSelection(null))}
                                    className={styles.MandateDropdown}
                                    disabled={isEmpty(draftDealsData)}
                                />
                            </div>
                            <div className={styles.Right}>
                                {lastUpdatedInfo && (
                                    <span className={styles.LastUpdatedInfo}>
                                        {lastUpdatedInfo.prefix} <strong style={{ color: '#4D4D4D' }}>{lastUpdatedInfo.highlight}</strong>
                                    </span>
                                )}
                                {showFilters && (
                                    <Button
                                        text={isEmpty(debtFilters) ? 'Add Filters' : 'Filters Applied'}
                                        onClick={() => {
                                            registerMixPanelForFiltersOnDebtOptions();
                                            handleActionWithUnsavedCheck(() => {
                                                dispatch(
                                                    setDealDrawer({
                                                        open: true,
                                                        drawerLabel: 'Filters',
                                                        drawerSource: 'Debt Products Filters',
                                                    }),
                                                );
                                            });
                                        }}
                                        icon={debtProdFilters}
                                        style={
                                            isEmpty(debtFilters)
                                                ? { marginTop: '0' }
                                                : {
                                                      marginTop: 0,
                                                      background: '#F2E8FF',
                                                      border: '1px solid transparent',
                                                  }
                                        }
                                        variant={'purpleinverted'}
                                        endIcon={isEmpty(debtFilters) ? null : PURPLE_CROSS}
                                        endIconClick={() => {
                                            handleActionWithUnsavedCheck(() => {
                                                dispatch(setDebtProductsFilters({}));
                                                fetchDebtProductsData({});
                                            });
                                        }}
                                    />
                                )}
                                {isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.EDIT_PROFILE) && (
                                    <Button
                                        text={
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.75rem',
                                                    width: '100%',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <span>Edit Profile</span>
                                                {activeCompletionPercentage !== undefined && (
                                                    <div className={styles.PercentageCompletion}>{activeCompletionPercentage}%</div>
                                                )}
                                            </div>
                                        }
                                        onClick={() => {
                                            handleActionWithUnsavedCheck(() => {
                                                dispatch(
                                                    setDealDrawer({
                                                        open: true,
                                                        drawerLabel: 'Edit Profile',
                                                        companyName: selectedCompany?.orgName,
                                                    }),
                                                );
                                                registerMixpanelForEditProfileClicked('Kebab Menu');
                                            });
                                        }}
                                        variant="purple"
                                        style={{ marginTop: '0' }}
                                        icon={editProfileIcon}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={styles.Body}>
                            <div
                                // style={selectionCount > 0 || draftSelectionCount > 0 ? { height: 'calc(100vh - 280px)' } : {}}
                                className={styles.TableContainer}
                            >
                                <table className={styles.Table}>
                                    <thead>
                                        <tr className={styles.HeadersRow}>
                                            {debtProductsData?.headers?.length ? (
                                                filterVisibleHeaders(debtProductsData?.headers, { isParentRow: true, isVisible }).map(
                                                    (header) => (
                                                        <th
                                                            key={header?.id}
                                                            className={clsx({
                                                                [styles.Header]: true,
                                                                [styles.SelectCol]: header.id === ENTITY_TYPE_KEYS.SELECT,
                                                                [styles.CaretCol]: header.id === ENTITY_TYPE_KEYS.CARET,
                                                                [styles.ProductsCol]: header.id === ENTITY_TYPE_KEYS.PRODUCT,
                                                                [styles.ProbabilityCol]: header.id === ENTITY_TYPE_KEYS.PROBABILITY,
                                                                [styles.SecurityCol]: header.id === ENTITY_TYPE_KEYS.SECURITY,
                                                                [styles.AmountCol]: header.id === ENTITY_TYPE_KEYS.AMOUNT,
                                                                [styles.TenureCol]: header.id === ENTITY_TYPE_KEYS.TENURE,
                                                                [styles.RoiCol]: header.id === ENTITY_TYPE_KEYS.ROI,
                                                                [styles.TimelineCol]: header.id === ENTITY_TYPE_KEYS.TIMELINE,
                                                                [styles.NoAccordionMode]: !isVisible(
                                                                    VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION,
                                                                ),
                                                            })}
                                                        >
                                                            {header.label}
                                                        </th>
                                                    ),
                                                )
                                            ) : (
                                                <th className={clsx(styles.Header, styles.EmptyHeader)}>{'-'}</th>
                                            )}
                                        </tr>
                                    </thead>
                                    {!loading && !showEmptyScreen && (
                                        <tbody className={styles.TableBody}>
                                            {debtProductsData?.rows?.map((rowData: DebtProductRowData, i) => (
                                                <React.Fragment key={i}>
                                                    <tr
                                                        key={`${i}-${rowData?.productId}`}
                                                        className={clsx(styles.TableRow, styles.ParentRow, {
                                                            [styles.ExpandedRow]: rowData?.expand,
                                                            [styles.NoAccordionMode]: !isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION),
                                                            [styles.SelectedRow]: !!productTerms[rowData.productId],
                                                            [styles.ActiveSelectionRow]:
                                                                !!productTerms[rowData.productId] &&
                                                                (productTerms[rowData.productId]?.isShared === 0 ||
                                                                    productTerms[rowData.productId]?.isShared === 2 ||
                                                                    productTerms[rowData.productId]?.isShared === undefined),
                                                        })}
                                                        onClick={() => {
                                                            if (isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION)) {
                                                                expandOrCollapseRow(rowData, true);
                                                            }
                                                        }}
                                                        onMouseEnter={() => setEditableRowHover(rowData, true)}
                                                        onMouseLeave={() => setEditableRowHover(null, true)}
                                                    >
                                                        {filterVisibleHeaders(debtProductsData?.headers, {
                                                            isParentRow: true,
                                                            isVisible,
                                                        }).map((header) => (
                                                            <React.Fragment key={header.id}>
                                                                {renderRowItem(header.id, rowData, true)}
                                                            </React.Fragment>
                                                        ))}
                                                    </tr>
                                                    {rowData?.expand &&
                                                        rowData?.lenders?.map((lenderData: DebtProductRowData, j) => (
                                                            <tr
                                                                key={j}
                                                                className={clsx(styles.TableRow, styles.Subrow, {
                                                                    [styles.NoAccordionMode]: !isVisible(
                                                                        VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION,
                                                                    ),
                                                                    [styles.SelectedRow]:
                                                                        lenderData.lenderId &&
                                                                        !!lenderTerms[lenderData.lenderId]?.[lenderData.productId],
                                                                    [styles.ActiveSelectionRow]:
                                                                        lenderData.lenderId &&
                                                                        !!lenderTerms[lenderData.lenderId]?.[lenderData.productId] &&
                                                                        (lenderTerms[lenderData.lenderId]?.[lenderData.productId]
                                                                            ?.isShared === 0 ||
                                                                            lenderTerms[lenderData.lenderId]?.[lenderData.productId]
                                                                                ?.isShared === 2 ||
                                                                            lenderTerms[lenderData.lenderId]?.[lenderData.productId]
                                                                                ?.isShared === undefined),
                                                                })}
                                                                onClick={() => {
                                                                    setShowDetailOverview(lenderData);
                                                                    registerMixpanelForUserHelpClick(rowData, false);
                                                                }}
                                                                onMouseEnter={() => setEditableRowHover(lenderData, false)}
                                                                onMouseLeave={() => setEditableRowHover(null, false)}
                                                            >
                                                                {filterVisibleHeaders(debtProductsData?.headers, {
                                                                    isParentRow: false,
                                                                    isVisible,
                                                                }).map((header) => (
                                                                    <React.Fragment key={header.id}>
                                                                        {renderRowItem(header.id, lenderData, false)}
                                                                    </React.Fragment>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    )}
                                </table>
                                {loading && showLoader()}
                                {!loading && showEmptyScreen && renderEmptyScreen()}
                            </div>
                        </div>
                    </div>
                </div>

                {showDetailOverview && (
                    <DebtProductOverview
                        data={showDetailOverview}
                        closeModal={() => setShowDetailOverview(null)}
                        percentageCompletion={percentageCompletion}
                        activeCompletionPercentage={activeCompletionPercentage}
                        selectedCompany={selectedCompany}
                        registerMixpanelForEditProfileClicked={registerMixpanelForEditProfileClicked}
                    />
                )}
            </div>
            {!isEmpty(debtProductsData.rows) && (
                <div
                    className={clsx(styles.Footer, {
                        [styles.FooterShift]: commentsThreadState.showCommentsThread && !commentsThreadState.closingCommentsThread,
                    })}
                    style={{
                        ...(!isEmpty(productTerms) || !isEmpty(lenderTerms)
                            ? { bottom: '0', opacity: '1', height: '4.5rem', visibility: 'visible' }
                            : {}),
                        ...(isCompanySide ? { left: '2.125rem' } : {}),
                    }}
                >
                    {isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.CREATE_DEAL) && (
                        <div className={styles.FooterText}>
                            <MandateAggregatedTermsComponent
                                aggregatedMandateTerms={aggregatedMandateTerms}
                                className={!isEmpty(productTerms) || !isEmpty(lenderTerms) ? mandateTermsStyles.animate : ''}
                            />
                            <div className={styles.FooterBtnContainer}>
                                {!savingDraft && renderClearOrDeleteButton(dealId)}
                                {savingDraft ? <AIText text={'Saving and adding it on Mandate'} /> : renderDealActionButton()}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <AskAicaV2 selectedCompany={selectedCompany} global />

            {showSuccessModal && (
                <DealSuccessModal
                    showSuccessModal={showSuccessModal}
                    heading="Mandate Created Successfully"
                    subHeading="You can share deal with lenders now."
                    buttonText={ButtonText.PROCEED}
                    queryParamsToClearOnClose={['createMandate']}
                    showCrossButton={true}
                />
            )}
            {dealDrawerState.open && (
                <ManageDealDrawer
                    source={dealDrawerState?.drawerSource ?? 'Debt Products'}
                    onClose={(payload) => {
                        fetchPECDataAndDebtProductsData(payload);
                        const isEditProfile = dealDrawerState?.drawerLabel === 'Edit Profile';
                        fetchCompanyMetrics(false, isEditProfile);
                    }}
                    dealId={dealId}
                    saveSelection={saveSelection}
                    fetchCompanyMetrics={fetchCompanyMetrics}
                    dealStatus={dealStatus}
                />
            )}

            {isMandateSharingDrawerOpen && (
                <MandateSharingDrawer
                    isOpen={isMandateSharingDrawerOpen}
                    onClose={() => setIsMandateSharingDrawerOpen(false)}
                    onShareWithLender={() => setIsShareWithLenderModalOpen(true)}
                    onEndUseOfCapitalChange={updateEndUseOfCapital}
                    initialEndUseOfCapital={dealTerms?.EndUseofCapital || ''}
                    dealId={dealId}
                    onUpdateSelectedUsers={updateSelectedUsers}
                    selectedUsers={selectedUsers}
                />
            )}

            {isShareWithLenderModalOpen && (
                <ShareWithLenderModal
                    open={isShareWithLenderModalOpen}
                    onClose={() => {
                        setIsShareWithLenderModalOpen(false);
                    }}
                    onSend={() => saveSelection(DealPayloadScenario.UPDATE_NOTES_DOCS)}
                    isTransitioning={isTransitioning}
                    hideContent={isTransitioning}
                    assesseeOrgId={assesseeOrgId}
                    addDealNote={addDealNote}
                    updateDealDocuments={updateDealDocuments}
                    companyName={selectedCompany?.orgName}
                    selectedCompany={selectedCompany}
                    dealId={dealId}
                />
            )}

            {showShareSuccessModal && (
                <DealSuccessModal
                    showSuccessModal={showShareSuccessModal}
                    heading="Deal Shared Successfully"
                    subHeading="Your mandate has been shared with the selected lenders"
                    buttonText={ButtonText.VIEW_DEALS}
                    buttonOnClick={() => {
                        setShowShareSuccessModal(false);
                        if (assesseeOrgId) {
                            navigate(NAVIGATION_CONSTANTS.COMPANY_DEALS_WITH_DEAL_UPDATED(assesseeOrgId), {
                                state: { fromDealSuccess: true },
                            });
                        } else {
                            navigate(NAVIGATION_CONSTANTS.DEALS_WITH_DEAL_UPDATED, {
                                state: { fromDealSuccess: true },
                            });
                        }
                    }}
                    showCrossButton={false}
                    onCLose={() => {
                        setShowShareSuccessModal(false);
                    }}
                    showContent={showShareSuccessModal}
                />
            )}

            {showSecurityModal && (
                <SecurityModal
                    open={!!showSecurityModal}
                    onClose={() => setShowSecurityModal(null)}
                    modalData={showSecurityModal}
                    onSave={updateSecurityOffered}
                />
            )}

            {showUnsavedChangesModal && isVisible(VISIBILITY_KEYS.DEBT_PRODUCT.CREATE_DEAL) && (
                <UnsavedChangesModal
                    isOpen={showUnsavedChangesModal}
                    onClose={handleCloseUnsavedModal}
                    onCloseWithoutSaving={handleCloseWithoutSaving}
                    onSaveChanges={() => handleSaveChanges(() => saveSelection(DealPayloadScenario.UPDATE_BASE))}
                />
            )}
        </>
    );
}

export default DebtProducts;
